import { toZonedTime, fromZonedTime, getTimezoneOffset } from 'date-fns-tz';
import { startOfDay } from 'date-fns';

import { expandedTimezoneList } from '@utils/expandedTimezoneList';
import { assertIsDefined } from '@utils/assertIsDefined';
import { TimeData } from '@components/TimeSelect';

const CURRENT_OFFSET = getTimezoneOffset(Intl.DateTimeFormat().resolvedOptions().timeZone);

const MOSCOW_OFFSET = getTimezoneOffset('Europe/Moscow');
const moscowTimezone = expandedTimezoneList.find(({ tzCode }) => tzCode === 'Europe/Moscow');
assertIsDefined(moscowTimezone, 'Static timezone list always contains Europe/Moscow');
const MOSCOW_UTC = moscowTimezone.utc;
const TIMEZONE_DIFF_IN_MINUTES = (MOSCOW_OFFSET - CURRENT_OFFSET) / 60000;

export type DateType = Date | string | number;

export class TimezoneHelper {
    public static readonly currentTimeZoneUtc: string =
        expandedTimezoneList.find(({ tzCode }) => tzCode === this.currentTimeZone)?.utc ?? MOSCOW_UTC;

    public static makeTimeDataFromLocalTimezone(value: DateType, toTimezone: string): TimeData {
        const date = this.toTimeZoneFromLocalTimezone(new Date(value), toTimezone);

        return {
            hour: date.getHours(),
            minutes: date.getMinutes(),
        };
    }

    public static setTimeDataFromTimezone(value: DateType, { hour, minutes }: TimeData, fromTimezone: string): Date {
        const date = new Date(value);
        date.setMinutes(minutes);
        date.setHours(hour);

        return this.zonedTimeToLocalTimezone(date, fromTimezone);
    }

    public static toTimeZoneFromLocalTimezone(valueInCurrentTimezone: DateType, toTimezone: string): Date {
        return toZonedTime(new Date(valueInCurrentTimezone), toTimezone);
    }

    public static zonedTimeToLocalTimezone(valueInSomeTimezone: DateType, fromTimezone: string): Date {
        return fromZonedTime(new Date(valueInSomeTimezone), fromTimezone);
    }

    public static toCalendarDay(instant: DateType, tzCode: string): Date {
        return startOfDay(toZonedTime(new Date(instant), tzCode));
    }

    public static fromCalendarDay(day: DateType, tzCode: string): Date {
        return fromZonedTime(startOfDay(new Date(day)), tzCode);
    }

    public static isMoscowTimeZone(): boolean {
        return TIMEZONE_DIFF_IN_MINUTES === 0;
    }

    public static isSameTimeZoneOffset(tzLeft: string, tzRight: string): boolean {
        return getTimezoneOffset(tzLeft) === getTimezoneOffset(tzRight);
    }

    public static get currentTimeZone(): string {
        let timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        if (!expandedTimezoneList.some(({ tzCode }) => tzCode === timeZone)) {
            timeZone = 'Europe/Moscow';
        }
        return timeZone;
    }
}
